import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { isPlanLock } from "../lib/plan";

export const CADENCES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "six_monthly", label: "Every six months" },
  { value: "annual", label: "Annual" },
];

// Each of these takes the shell's Business reviews organization filter as an argument.
// "" means every organization, which is the default and what the endpoints already did.
export function useReviewSchedule(organizationId = "") {
  const [schedule, setSchedule] = useState([]);
  const [counts, setCounts] = useState({});
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
      const data = await api.get(`s4/api/review-schedule${qs}`);
      setSchedule(data?.schedule || []);
      setCounts(data?.counts || {});
      setToday(data?.today || "");
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the review schedule.");
    } finally { setLoading(false); }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  // `orgId`, not `organizationId`: the hook now takes an organizationId of its own for the
  // filter, and a parameter of the same name would shadow it — silently doing the right
  // thing here while making any later use of the filter inside this callback wrong.
  const setCadence = useCallback(async (orgId, cadence) => {
    try {
      if (!cadence) await api.del(`s4/api/review-schedule/${orgId}`);
      else await api.put(`s4/api/review-schedule/${orgId}`, { cadence });
      await load();
    } catch (e) {
      setError(e.message || "Could not save that cadence.");
    }
  }, [load]);

  return { schedule, counts, today, loading, error, reload: load, setCadence };
}

export function useLatestScans(search, organizationId = "") {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (organizationId) params.set("organization_id", organizationId);
        const qs = params.toString();
        const data = await api.get(`s4/api/latest-scans${qs ? `?${qs}` : ""}`);
        if (!cancelled) { setScans(data?.scans || []); setError(""); }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load the scans.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, organizationId]);

  return { scans, loading, error };
}

export function useReviewOverview(organizationId = "") {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
        setData(await api.get(`s4/api/review-overview${qs}`));
        setError("");
      }
      catch (e) { setError(e.message || "Could not load the overview."); }
      finally { setLoading(false); }
    })();
  }, [organizationId]);

  return { ...(data || {}), loading, error };
}

// Organizations, seen from the review side: the scanner's own list plus the fields a
// review carries. Not a second list — the ids are the scanner's.
// `enabled` false skips the request outright — a plan without Business Reviews would
// only get a 403 for it — and reports the review side as simply absent, not failed.
export function useReviewOrganizations(enabled = true) {
  const [organizations, setOrganizations] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ search: "", relationship: "", health: "", status: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [locked, setLocked] = useState(!enabled);

  const load = useCallback(async () => {
    if (!enabled) { setLocked(true); setLoading(false); return; }
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
      const data = await api.get(`s4/api/review-organizations${qs ? `?${qs}` : ""}`);
      setOrganizations(data?.organizations || []);
      setKpis(data?.kpis || null);
      setCounts(data?.counts || {});
      setError("");
      setLocked(false);
    } catch (e) {
      // A plan lock is not an outage: the review columns are just not part of this plan.
      if (isPlanLock(e)) { setLocked(true); setError(""); }
      else setError(e.message || "Could not load the organizations.");
    } finally { setLoading(false); }
  }, [filters, enabled]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = useCallback(async (organizationId, patch) => {
    try {
      await api.put(`s4/api/review-organizations/${organizationId}`, patch);
      await load();
      return true;
    } catch (e) {
      setError(e.message || "Could not save that.");
      return false;
    }
  }, [load]);

  return { organizations, kpis, counts, filters, setFilters, loading, error, locked, reload: load, saveProfile };
}
